import {
    createAsyncThunk,
    createSlice,
    PayloadAction,
    createSelector,
} from "@reduxjs/toolkit"
import type { AxiosError } from "axios"
import { workflowService } from "@/modules/onboarding/infrastructure/workflow-service"
import type {
    WorkflowDto,
    WorkflowTaskDto,
    UpsertWorkflowDto,
    UpsertWorkflowTaskDto,
} from "@/modules/onboarding/workflows/dtos/workflow.dto"
import { RootState } from "@/store"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"

interface WorkflowsState {
    items: WorkflowDto[]
    selectedWorkflow?: WorkflowDto | null
    tasksByWorkflow: Record<string, WorkflowTaskDto[]>
    isLoading: boolean
    error?: string | null
}

const initialState: WorkflowsState = {
    items: [],
    selectedWorkflow: null,
    tasksByWorkflow: {},
    isLoading: false,
    error: null,
}

const requireOrgId = (state: RootState) => {
    const orgId = selectCurrentOrganizationId(state)
    if (!orgId) throw new Error("No organisation selected")
    return orgId
}

const extractErrorMessage = (error: unknown): string => {
    const axiosError = error as AxiosError<{ error?: string }>
    return axiosError.response?.data?.error ?? (error as Error).message ?? "An unexpected error occurred"
}

export const fetchWorkflows = createAsyncThunk<
    WorkflowDto[],
    void,
    { state: RootState }
>("workflows/fetchAll", async (_, { getState, rejectWithValue }) => {
    try {
        const orgId = requireOrgId(getState())
        return await workflowService.getWorkflows(orgId, true)
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error))
    }
})

export const createWorkflow = createAsyncThunk<
    WorkflowDto,
    UpsertWorkflowDto,
    { state: RootState }
>("workflows/create", async (payload, { getState, rejectWithValue }) => {
    try {
        const orgId = requireOrgId(getState())
        return await workflowService.createWorkflow(orgId, payload)
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error))
    }
})

export const updateWorkflow = createAsyncThunk<
    WorkflowDto,
    { id: string; payload: UpsertWorkflowDto },
    { state: RootState }
>(
    "workflows/update",
    async ({ id, payload }, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            return await workflowService.updateWorkflow(orgId, id, payload)
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const deleteWorkflow = createAsyncThunk<
    string,
    string,
    { state: RootState }
>("workflows/delete", async (id, { getState, rejectWithValue }) => {
    try {
        const orgId = requireOrgId(getState())
        await workflowService.deleteWorkflow(orgId, id)
        return id
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error))
    }
})

export const fetchTasks = createAsyncThunk<
    WorkflowTaskDto[],
    string,
    { state: RootState }
>("workflows/fetchTasks", async (workflowId, { getState, rejectWithValue }) => {
    try {
        const orgId = requireOrgId(getState())
        return await workflowService.getTasks(orgId, workflowId)
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error))
    }
})

export const createTask = createAsyncThunk<
    WorkflowTaskDto,
    { workflowId: string; payload: UpsertWorkflowTaskDto },
    { state: RootState }
>(
    "workflows/createTask",
    async ({ workflowId, payload }, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            return await workflowService.createTask(orgId, workflowId, payload)
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const updateTask = createAsyncThunk<
    WorkflowTaskDto,
    { workflowId: string; taskId: string; payload: UpsertWorkflowTaskDto },
    { state: RootState }
>(
    "workflows/updateTask",
    async ({ workflowId, taskId, payload }, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            return await workflowService.updateTask(
                orgId,
                workflowId,
                taskId,
                payload,
            )
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const deleteTask = createAsyncThunk<
    string,
    { workflowId: string; taskId: string },
    { state: RootState }
>(
    "workflows/deleteTask",
    async ({ workflowId, taskId }, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            await workflowService.deleteTask(orgId, workflowId, taskId)
            return taskId
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const startWorkflow = createAsyncThunk<
    unknown,
    {
        workflowId: string
        contextType: "Employee" | "Standalone"
        employeeId?: string
        contextDescription?: string
        batchId?: string
        batchName?: string
        taskDueDateOverrides?: { workflowTaskId: string; dueDate: string | null }[]
    },
    { state: RootState }
>(
    "workflows/start",
    async (
        {
            workflowId,
            contextType,
            employeeId,
            contextDescription,
            batchId,
            batchName,
            taskDueDateOverrides,
        },
        { getState, rejectWithValue },
    ) => {
        try {
            const orgId = requireOrgId(getState())
            return await workflowService.startWorkflow(orgId, workflowId, {
                contextType,
                employeeId,
                contextDescription,
                batchId,
                batchName,
                taskDueDateOverrides,
            })
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

const workflowsSlice = createSlice({
    name: "workflows",
    initialState,
    reducers: {
        setSelectedWorkflow(state, action: PayloadAction<WorkflowDto | null>) {
            state.selectedWorkflow = action.payload
        },
        clearError(state) {
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkflows.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchWorkflows.fulfilled, (state, action) => {
                state.isLoading = false
                state.items = action.payload
            })
            .addCase(fetchWorkflows.rejected, (state, action) => {
                state.isLoading = false
                state.error = (action.payload as string) || action.error.message || null
            })

            .addCase(createWorkflow.fulfilled, (state, action) => {
                state.items.push(action.payload)
            })
            .addCase(updateWorkflow.fulfilled, (state, action) => {
                state.items = state.items.map((w) =>
                    w.id === action.payload.id ? action.payload : w,
                )
                if (state.selectedWorkflow?.id === action.payload.id) {
                    state.selectedWorkflow = action.payload
                }
            })
            .addCase(deleteWorkflow.fulfilled, (state, action) => {
                state.items = state.items.filter((w) => w.id !== action.payload)
                delete state.tasksByWorkflow[action.payload]
                if (state.selectedWorkflow?.id === action.payload)
                    state.selectedWorkflow = null
            })

            .addCase(fetchTasks.fulfilled, (state, action) => {
                if (state.selectedWorkflow) {
                    state.tasksByWorkflow[state.selectedWorkflow.id] = action.payload
                }
            })
            .addCase(createTask.fulfilled, (state, action) => {
                const workflowId = action.payload.workflowId
                const list = state.tasksByWorkflow[workflowId] || []
                state.tasksByWorkflow[workflowId] = [...list, action.payload]
            })
            .addCase(updateTask.fulfilled, (state, action) => {
                const workflowId = action.payload.workflowId
                const list = state.tasksByWorkflow[workflowId] || []
                state.tasksByWorkflow[workflowId] = list.map((t) =>
                    t.id === action.payload.id ? action.payload : t,
                )
            })
            .addCase(deleteTask.fulfilled, (state, action) => {
                const workflowId = state.selectedWorkflow?.id
                if (!workflowId) return
                state.tasksByWorkflow[workflowId] = (
                    state.tasksByWorkflow[workflowId] || []
                ).filter((t) => t.id !== action.payload)
            })
    },
})

export const { setSelectedWorkflow, clearError } = workflowsSlice.actions

export const selectWorkflowsState = (state: RootState) => state.workflows
export const selectWorkflows = (state: RootState) => state.workflows.items
export const selectSelectedWorkflow = (state: RootState) =>
    state.workflows.selectedWorkflow
export const selectTasksForSelected = createSelector(
    [
        selectSelectedWorkflow,
        (state: RootState) => state.workflows.tasksByWorkflow,
    ],
    (selectedWorkflow, tasksByWorkflow) => {
        const wfId = selectedWorkflow?.id
        if (!wfId) return []
        return tasksByWorkflow[wfId] || []
    },
)

export default workflowsSlice.reducer
